/**
 * Tutoriel affiché à la première visite : comment obtenir la clé d'accès
 * (le mot de passe d'application) et la donner à Scanyt.
 *
 * Chaque messagerie a son parcours illustré pas à pas. Les écrans des
 * messageries sont des illustrations (tutoriel/maquettes.html), ceux de
 * Scanyt de vraies captures ; tous sont fabriqués par tutoriel/capturer.mjs.
 * À la fin, la messagerie choisie est reportée dans le formulaire.
 */
import { FOURNISSEURS } from './fournisseurs.js';

const CLE_VU = 'scanyt-tutoriel-vu';
const img = (nom) => `tutoriel/${nom}.png`;

/** Yahoo et AOL partagent les mêmes écrans de compte. */
function parcoursYahoo(prefixe, marque, lienSecurite) {
  return [
    {
      titre: 'Ouvre la sécurité de ton compte',
      texte: `Ouvre la page <strong>« Sécurité du compte »</strong> de ${marque}. Dans la partie `
        + '<strong>« Connexions externes »</strong>, clique sur <strong>« Créer un mot de passe d’application »</strong>.',
      note: `${marque} conseille de le faire depuis un navigateur où tu te connectes d’habitude, pas en navigation privée.`,
      image: img(`${prefixe}-securite`),
      alt: `Page « Sécurité du compte » de ${marque}, avec le lien « Créer un mot de passe d’application » entouré.`,
      lien: { href: lienSecurite, texte: 'Ouvrir la sécurité du compte' }
    },
    {
      titre: 'Crée ta clé d’accès',
      texte: 'Dans le champ, écris <strong>Scanyt</strong>, puis clique sur <strong>« Générer le mot de passe »</strong>.',
      image: img(`${prefixe}-nom`),
      alt: 'Le nom Scanyt est saisi et le bouton « Générer le mot de passe » est entouré.'
    },
    {
      titre: 'Copie le code de 16 lettres',
      texte: `${marque} affiche ta clé d’accès : <strong>16 lettres</strong>. Sélectionne-la et copie-la, puis clique sur <strong>« Terminé »</strong>.`,
      note: 'Elle n’est montrée qu’une seule fois : copie-la avant de fermer la fenêtre.',
      image: img(`${prefixe}-code`),
      alt: 'Fenêtre affichant un mot de passe d’application de 16 lettres, surligné.'
    }
  ];
}

/** Messageries qui acceptent le mot de passe habituel. */
function parcoursMotDePasse(nom) {
  return [{
    titre: 'Pas de clé à créer',
    texte: `${nom} accepte directement <strong>le mot de passe de ta messagerie</strong>, celui que tu utilises `
      + 'pour lire tes emails sur son site. Rien à faire de ce côté : passe à l’étape suivante.',
    note: 'Mot de passe oublié ? Réinitialise-le depuis le site de ta messagerie, puis reviens ici.'
  }];
}

const PARCOURS = {
  gmail: [
    {
      titre: 'Active la validation en deux étapes',
      texte: 'Google ne donne de clé d’accès qu’aux comptes protégés par la validation en deux étapes. '
        + 'Ouvre la page ci-dessous, clique sur <strong>« Activer la validation en deux étapes »</strong> et suis les instructions de Google.',
      note: 'Déjà activée ? Passe directement à l’étape suivante.',
      image: img('google-2sv'),
      alt: 'Page Google « Validation en deux étapes », avec le bouton « Activer la validation en deux étapes » entouré.',
      lien: { href: 'https://myaccount.google.com/signinoptions/twosv', texte: 'Ouvrir la validation en deux étapes' }
    },
    {
      titre: 'Crée ta clé d’accès',
      texte: 'Ouvre la page <strong>« Mots de passe des applications »</strong>. Dans le champ, écris '
        + '<strong>Scanyt</strong>, puis clique sur <strong>« Créer »</strong>.',
      note: 'Google peut te redemander ton mot de passe : c’est normal.',
      image: img('google-creer'),
      alt: 'Page Google « Mots de passe des applications » : le nom Scanyt est saisi et le bouton « Créer » est entouré.',
      lien: { href: 'https://myaccount.google.com/apppasswords', texte: 'Ouvrir les mots de passe des applications' }
    },
    {
      titre: 'Copie le code de 16 lettres',
      texte: 'Google affiche ta clé d’accès dans une fenêtre : <strong>16 lettres</strong> sur fond jaune. '
        + 'Sélectionne-la et copie-la.',
      note: 'Google ne la montre qu’une seule fois : ne ferme pas la fenêtre avant de l’avoir collée dans Scanyt. '
        + 'Les espaces n’ont pas d’importance.',
      image: img('google-code'),
      alt: 'Fenêtre Google « Mot de passe d’application généré » avec un code de 16 lettres surligné.'
    }
  ],
  outlook: [
    {
      titre: 'Clique sur « Se connecter avec Microsoft »',
      texte: 'Avec Outlook, il n’y a <strong>aucun mot de passe à créer</strong>. Choisis « Outlook / Hotmail / Live », '
        + 'puis clique sur le bouton <strong>« Se connecter avec Microsoft »</strong>.',
      note: 'Une fenêtre Microsoft s’ouvre. Si rien ne s’affiche, autorise les fenêtres pour ce site.',
      image: img('scanyt-coller-outlook'),
      alt: 'Formulaire de Scanyt avec Outlook choisi : le bouton « Se connecter avec Microsoft » est entouré.'
    },
    {
      titre: 'Connecte-toi et accepte',
      texte: 'Dans la fenêtre Microsoft, connecte-toi à ton compte, puis clique sur <strong>« Accepter »</strong> '
        + 'pour autoriser Scanyt à lire ta boîte.',
      note: 'C’est sur la page de Microsoft que tu tapes ton mot de passe : Scanyt ne le voit jamais. '
        + 'Microsoft parle d’accès à tes e-mails, mais Scanyt ouvre ta boîte en lecture seule.',
      image: img('microsoft-accord'),
      alt: 'Écran Microsoft « Autoriser cette application à accéder à vos informations ? » avec le bouton « Accepter » entouré.'
    },
    {
      titre: 'Lance le scan',
      texte: 'De retour sur Scanyt, ton adresse s’affiche avec <strong>« ✓ Connecté »</strong>. '
        + 'Clique sur <strong>« Lancer le scan »</strong>.',
      note: 'L’accès est temporaire et n’est jamais enregistré. Tu peux le retirer à tout moment dans ton compte Microsoft, '
        + 'rubrique « Confidentialité » › « Applications et services ».',
      image: img('scanyt-lancer-outlook'),
      alt: 'Formulaire de Scanyt connecté à Microsoft, avec le bouton « Lancer le scan » entouré.'
    }
  ],
  yahoo: parcoursYahoo('yahoo', 'Yahoo', 'https://login.yahoo.com/account/security'),
  aol: parcoursYahoo('aol', 'AOL', 'https://login.aol.com/account/security'),
  icloud: [
    {
      titre: 'Ouvre ton compte Apple',
      texte: 'Connecte-toi sur le site du compte Apple. Dans <strong>« Connexion et sécurité »</strong>, '
        + 'choisis <strong>« Mots de passe pour applications »</strong>.',
      note: 'L’identification à deux facteurs doit être activée : c’est le cas sur la plupart des comptes Apple.',
      image: img('apple-section'),
      alt: 'Page « Connexion et sécurité » du compte Apple, avec la ligne « Mots de passe pour applications » entourée.',
      lien: { href: 'https://account.apple.com/account/manage', texte: 'Ouvrir mon compte Apple' }
    },
    {
      titre: 'Crée ta clé d’accès',
      texte: 'Clique sur <strong>« Générer un mot de passe pour application »</strong>, écris <strong>Scanyt</strong>, '
        + 'puis clique sur <strong>« Créer »</strong>.',
      note: 'Apple peut te redemander le mot de passe de ton compte : c’est normal.',
      image: img('apple-nom'),
      alt: 'Le nom Scanyt est saisi et le bouton « Créer » est entouré.'
    },
    {
      titre: 'Copie le code',
      texte: 'Apple affiche ta clé d’accès sous la forme <strong>xxxx-xxxx-xxxx-xxxx</strong>. Copie-la en entier, tirets compris.',
      note: 'Elle n’est montrée qu’une seule fois : copie-la avant de fermer la fenêtre.',
      image: img('apple-code'),
      alt: 'Fenêtre Apple affichant un mot de passe pour application au format xxxx-xxxx-xxxx-xxxx, surligné.'
    }
  ],
  zoho: [
    {
      titre: 'Ouvre la sécurité de ton compte Zoho',
      texte: 'Sur ton compte Zoho, va dans <strong>« Sécurité »</strong>, puis <strong>« Mots de passe d’application »</strong>, '
        + 'et clique sur <strong>« Générer un nouveau mot de passe »</strong>.',
      image: img('zoho-section'),
      alt: 'Page Zoho « Mots de passe d’application », avec le bouton « Générer un nouveau mot de passe » entouré.',
      lien: { href: 'https://accounts.zoho.eu/home#security/app_password', texte: 'Ouvrir la sécurité Zoho' }
    },
    {
      titre: 'Crée ta clé d’accès',
      texte: 'Écris <strong>Scanyt</strong> comme nom d’application, puis clique sur <strong>« Générer »</strong>.',
      image: img('zoho-nom'),
      alt: 'Le nom Scanyt est saisi et le bouton « Générer » est entouré.'
    },
    {
      titre: 'Copie le code',
      texte: 'Zoho affiche ta clé d’accès : <strong>12 caractères</strong>. Sélectionne-la et copie-la.',
      note: 'Zoho ne la montre qu’une seule fois : copie-la avant de fermer la fenêtre.',
      image: img('zoho-code'),
      alt: 'Fenêtre Zoho affichant un mot de passe d’application de 12 caractères, surligné.'
    }
  ],
  gmx: [{
    titre: 'Autorise l’accès IMAP',
    texte: 'GMX bloque par défaut la lecture de ta boîte par d’autres applications. Dans les paramètres de ta messagerie, '
      + 'ouvre <strong>« POP3 & IMAP »</strong>, coche la case qui autorise l’accès, puis clique sur <strong>« Enregistrer »</strong>.',
    note: 'Ensuite, Scanyt utilise simplement ton mot de passe GMX. GMX peut désactiver cet accès s’il reste longtemps inutilisé.',
    image: img('gmx-imap'),
    alt: 'Paramètres GMX « POP3 & IMAP », avec la case « Autoriser l’accès POP3 et IMAP » cochée et le bouton « Enregistrer » entourés.'
  }],
  orange: parcoursMotDePasse('Orange'),
  free: parcoursMotDePasse('Free'),
  bbox: parcoursMotDePasse('Bbox'),
  sfr: parcoursMotDePasse('SFR'),
  laposte: parcoursMotDePasse('La Poste')
};

/** Ces messageries donnent une clé à part ; les autres, le mot de passe habituel. */
const AVEC_CLE = new Set(['gmail', 'yahoo', 'aol', 'icloud', 'zoho']);

function etapesFin(cle) {
  if (FOURNISSEURS[cle]?.microsoft) return []; // son parcours se termine déjà par le scan
  const avecCle = AVEC_CLE.has(cle);
  return [
    {
      titre: avecCle ? 'Colle-la dans Scanyt' : 'Saisis-le dans Scanyt',
      texte: avecCle
        ? 'Reviens sur Scanyt. Écris ton <strong>adresse email</strong>, puis colle la clé dans le champ '
          + '<strong>« Mot de passe d’application »</strong>.'
        : 'Écris ton <strong>adresse email</strong>, puis ton <strong>mot de passe</strong> de messagerie dans le champ '
          + '<strong>« Mot de passe d’application »</strong>.',
      note: avecCle
        ? 'C’est une clé à part : ton vrai mot de passe ne quitte jamais ta messagerie.'
        : 'Il ne sert que le temps du scan et n’est jamais enregistré.',
      image: img(`scanyt-coller-${cle}`),
      alt: 'Formulaire de Scanyt : les champs « Adresse email » et « Mot de passe d’application » sont entourés.'
    },
    {
      titre: 'Lance le scan',
      texte: 'Clique sur <strong>« Lancer le scan »</strong>. Scanyt lit ta boîte directement depuis ce navigateur, '
        + 'en connexion chiffrée, et liste les comptes créés avec ton adresse.',
      note: avecCle
        ? 'La clé ne sert que le temps du scan et n’est jamais enregistrée. Tu pourras la supprimer chez ta messagerie ensuite.'
        : 'Ton mot de passe ne sert que le temps du scan et n’est jamais enregistré.',
      image: img('scanyt-lancer'),
      alt: 'Formulaire de Scanyt rempli, avec le bouton « Lancer le scan » entouré.'
    }
  ];
}

function lireVu() {
  try { return localStorage.getItem(CLE_VU) === '1'; } catch { return false; }
}

function marquerVu() {
  try { localStorage.setItem(CLE_VU, '1'); } catch { /* stockage refusé */ }
}

function element(balise, attributs = {}, enfants = []) {
  const e = Object.assign(document.createElement(balise), attributs);
  e.append(...enfants);
  return e;
}

export function installerTutoriel() {
  let fournisseur = 'gmail';
  let index = 0;

  const corps = element('div', { className: 'tuto-corps' });
  const points = element('div', { className: 'tuto-points', ariaHidden: 'true' });
  const compteur = element('span', { className: 'tuto-compteur' });
  const passer = element('button', { type: 'button', className: 'tuto-passer', textContent: 'Passer' });
  const precedent = element('button', { type: 'button', className: 'secondary', textContent: 'Précédent' });
  const suivant = element('button', { type: 'button', className: 'tuto-suivant', textContent: 'Suivant' });
  const dialogue = element('dialog', { className: 'tuto', ariaLabel: 'Tutoriel : se connecter à Scanyt' }, [
    element('div', { className: 'tuto-entete' }, [compteur, points]),
    corps,
    element('div', { className: 'tuto-actions' }, [passer, precedent, suivant])
  ]);
  document.body.append(dialogue);

  const etapes = () => [{ accueil: true }, ...(PARCOURS[fournisseur] || []), ...etapesFin(fournisseur)];

  function accueil() {
    const choix = element('select', { id: 'tuto-fournisseur' });
    for (const [cle, f] of Object.entries(FOURNISSEURS)) {
      choix.append(element('option', { value: cle, textContent: f.label, selected: cle === fournisseur }));
    }
    choix.addEventListener('change', () => { fournisseur = choix.value; precharger(); afficherProgression(); });
    return [
      element('h2', { textContent: 'Bienvenue sur Scanyt 👋' }),
      element('p', {
        innerHTML: 'Scanyt retrouve tous les comptes créés avec ton adresse email. '
          + 'Pour lire ta boîte, il a besoin d’une <strong>clé d’accès</strong> : un mot de passe spécial, '
          + 'créé chez ta messagerie, que tu peux supprimer quand tu veux.'
      }),
      element('ol', { className: 'tuto-resume' }, [
        element('li', { textContent: 'Tu crées la clé chez ta messagerie' }),
        element('li', { textContent: 'Tu la colles dans Scanyt' }),
        element('li', { textContent: 'Tu lances le scan' })
      ]),
      element('div', { className: 'field' }, [
        element('label', { htmlFor: 'tuto-fournisseur', textContent: 'Quelle messagerie utilises-tu ?' }),
        choix
      ]),
      element('p', { className: 'tuto-note', textContent: 'Compte environ 2 minutes. Tu peux revoir ce guide à tout moment.' }),
      // Envie de voir avant de se lancer : quelques comptes fictifs suffisent.
      window.lancerDemo ? element('div', { className: 'tuto-demo' }, [
        element('span', { textContent: 'Pas encore prêt ?' }),
        Object.assign(element('button', { type: 'button', className: 'secondary', textContent: 'Voir une démo d’abord' }), {
          onclick: () => { marquerVu(); dialogue.close(); window.lancerDemo(); }
        })
      ]) : null
    ].filter(Boolean);
  }

  function etape(e) {
    const contenu = [element('h2', { textContent: e.titre }), element('p', { innerHTML: e.texte })];
    if (e.lien) {
      contenu.push(element('a', {
        className: 'tuto-lien', href: e.lien.href, target: '_blank', rel: 'noopener noreferrer',
        textContent: e.lien.texte + ' ↗'
      }));
    }
    if (e.image) {
      contenu.push(element('figure', { className: 'tuto-image' }, [
        element('img', { src: e.image, alt: e.alt, loading: 'eager', decoding: 'async' })
      ]));
    }
    if (e.note) contenu.push(element('p', { className: 'tuto-note', textContent: e.note }));
    return contenu;
  }

  function afficherProgression() {
    const liste = etapes();
    compteur.textContent = `Étape ${index + 1} sur ${liste.length}`;
    points.replaceChildren(...liste.map((_, i) => element('span', { className: i === index ? 'actif' : i < index ? 'fait' : '' })));
    const derniere = index === liste.length - 1;
    suivant.textContent = derniere ? 'C’est parti !' : 'Suivant';
    passer.hidden = derniere;
    precedent.hidden = index === 0;
  }

  /** sens : 1 en avançant, -1 en reculant — l'étape arrive du côté d'où l'on vient. */
  function afficher(sens = 1) {
    const liste = etapes();
    const e = liste[index];
    corps.replaceChildren(...(e.accueil ? accueil() : etape(e)));
    corps.scrollTop = 0;
    // Relance l'animation d'entrée, même si l'étape précédente n'a pas fini.
    corps.classList.remove('entree', 'retour');
    void corps.offsetWidth;
    corps.classList.add('entree');
    if (sens < 0) corps.classList.add('retour');
    afficherProgression();
  }

  function avancer() {
    if (index < etapes().length - 1) { index++; afficher(1); } else fermer();
  }

  function reculer() {
    if (index > 0) { index--; afficher(-1); }
  }

  function fermer() {
    marquerVu();
    const select = document.getElementById('provider');
    if (select && [...select.options].some((o) => o.value === fournisseur) && select.value !== fournisseur) {
      select.value = fournisseur;
      select.dispatchEvent(new Event('change'));
    }
    dialogue.close();
    const email = document.getElementById('email');
    if (email) (email.value ? document.getElementById('password') : email).focus();
  }

  suivant.addEventListener('click', () => { avancer(); if (dialogue.open) suivant.focus(); });
  precedent.addEventListener('click', () => { reculer(); (precedent.hidden ? suivant : precedent).focus(); });
  passer.addEventListener('click', fermer);
  // Les flèches du clavier tournent les pages, sauf dans le menu des messageries.
  dialogue.addEventListener('keydown', (evenement) => {
    if (evenement.target.tagName === 'SELECT') return;
    if (evenement.key === 'ArrowRight') { evenement.preventDefault(); avancer(); }
    if (evenement.key === 'ArrowLeft') { evenement.preventDefault(); reculer(); }
  });
  // Échap ferme la fenêtre comme « Passer ».
  dialogue.addEventListener('cancel', (evenement) => { evenement.preventDefault(); fermer(); });

  // Les images de la messagerie choisie sont chargées d'avance : elles sont
  // prêtes quand on arrive à leur étape, sans que la fenêtre change de taille.
  function precharger() {
    for (const e of etapes()) {
      if (e.image) new Image().src = e.image;
    }
  }

  function ouvrir() {
    index = 0;
    const select = document.getElementById('provider');
    if (select && FOURNISSEURS[select.value]) fournisseur = select.value;
    precharger();
    afficher();
    dialogue.showModal();
    suivant.focus();
  }

  // Un lien pour le revoir, sous l'avertissement du formulaire.
  const note = document.querySelector('#scan-form-card .inline-note');
  if (note) {
    const revoir = element('button', { type: 'button', className: 'tuto-revoir', textContent: 'Besoin d’aide pour te connecter ? Voir le guide pas à pas' });
    revoir.addEventListener('click', ouvrir);
    note.after(revoir);
  }

  if (!lireVu()) ouvrir();
}
